import { Link } from "react-router-dom";
import { useRef, useState, useEffect } from "react";
import Hls from "hls.js";
import { Radio, Tv, Eye, BadgeCheck, MapPin, Trophy, Share2 } from "lucide-react";
import { useBadges } from "@/context/BadgesContext";
import { API } from "@/lib/api";
import { toast } from "sonner";

const HOVER_CAPABLE = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(hover: hover)").matches;

// Ensures only one radio hover-preview plays at a time across all cards.
let activeAudio = null;

export default function MediaCard({ media, index = 0 }) {
  const isTv = media.type === "tv";
  const path = `/${isTv ? "tv" : "radio"}/${media.slug}`;
  const count = isTv ? media.views : media.listens;
  const fallback = `https://ui-avatars.com/api/?name=${encodeURIComponent(media.name)}&background=00A3E0&color=fff&bold=true&size=256`;
  const { labelFor } = useBadges();
  const topLabel = labelFor?.(media.id);

  const [preview, setPreview] = useState(false);
  const [audioPreview, setAudioPreview] = useState(false);
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const canPreview = isTv && !!media.hls_url && media.hls_url.toLowerCase().includes(".m3u8") && HOVER_CAPABLE;
  const canPreviewAudio = !isTv && !!media.audio_url && HOVER_CAPABLE;

  useEffect(() => {
    if (!preview || !canPreview) return;
    const v = videoRef.current;
    if (!v) return;
    let hls;
    v.muted = true;
    if (Hls.isSupported()) {
      hls = new Hls({ enableWorker: true });
      hls.loadSource(media.hls_url);
      hls.attachMedia(v);
      v.play?.().catch(() => {});
    } else if (v.canPlayType("application/vnd.apple.mpegurl")) {
      v.src = media.hls_url;
      v.play?.().catch(() => {});
    }
    return () => { if (hls) { hls.detachMedia(); hls.destroy(); } if (v) { v.pause(); v.removeAttribute("src"); } };
  }, [preview, canPreview, media.hls_url]);

  useEffect(() => {
    if (!audioPreview || !canPreviewAudio) return;
    const a = audioRef.current;
    if (!a) return;
    if (activeAudio && activeAudio !== a) activeAudio.pause();
    activeAudio = a;
    a.volume = 0.35;
    if (a.src !== media.audio_url) a.src = media.audio_url;
    a.play().catch(() => {});
    return () => { a.pause(); if (activeAudio === a) activeAudio = null; };
  }, [audioPreview, canPreviewAudio, media.audio_url]);

  const handleShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const shareUrl = `${API}/share/${media.type}/${media.slug}`;
    const text = `Découvrez ${media.name} sur Les Chaînes Numériques de la RDC`;
    if (navigator.share) {
      try { await navigator.share({ title: media.name, text, url: shareUrl }); } catch { /* cancelled */ }
    } else {
      try { await navigator.clipboard.writeText(shareUrl); toast.success("Lien copié !"); }
      catch { toast.error("Impossible de copier le lien"); }
    }
  };

  const onEnter = () => { if (canPreview) setPreview(true); if (canPreviewAudio) setAudioPreview(true); };
  const onLeave = () => { setPreview(false); setAudioPreview(false); };

  return (
    <Link
      to={path}
      data-testid={`media-card-${media.slug}`}
      className="group block rounded-2xl bg-card border border-border overflow-hidden hover-lift hover:shadow-xl hover:border-primary/40 fade-up"
      style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
    >
      <div className="relative aspect-video overflow-hidden bg-muted" onMouseEnter={onEnter} onMouseLeave={onLeave}>
        <img
          src={media.banner || fallback}
          alt={media.name}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {preview && canPreview && (
          <video ref={videoRef} muted playsInline poster={media.banner} className="absolute inset-0 w-full h-full object-cover" data-testid={`card-preview-${media.slug}`} />
        )}
        {audioPreview && canPreviewAudio && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-end gap-1 h-7 text-rdcgold drop-shadow" data-testid={`card-audio-preview-${media.slug}`}>
            {[0, 1, 2, 3, 4].map((k) => <span key={k} className="eq-bar w-1.5" style={{ animationDelay: `${k * 0.13}s` }} />)}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5">
          {media.is_live ? (
            <span className="live-dot flex items-center gap-1 rounded-full bg-rdcred px-2 py-0.5 text-[10px] font-mono font-bold uppercase tracking-wider text-white">
              <span className="w-1.5 h-1.5 rounded-full bg-white" /> Direct
            </span>
          ) : (
            <span className="rounded-full bg-black/50 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-white/80">
              Hors ligne
            </span>
          )}
        </div>
        <span
          className={`absolute top-2.5 right-2.5 flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
            isTv ? "bg-rdcblue text-white" : "bg-rdcgold text-amber-950"
          }`}
        >
          {isTv ? <Tv size={11} /> : <Radio size={11} />} {isTv ? "TV" : "Radio"}
        </span>
        {topLabel && (
          <span data-testid={`top-badge-${media.slug}`} className="absolute top-9 right-2.5 flex items-center gap-1 rounded-full bg-gradient-to-r from-rdcgold to-amber-500 text-amber-950 px-2 py-0.5 text-[10px] font-bold uppercase shadow">
            <Trophy size={10} /> {topLabel}
          </span>
        )}
        <button
          type="button"
          onClick={handleShare}
          data-testid={`card-share-${media.slug}`}
          title="Partager cette chaîne"
          aria-label="Partager"
          className="absolute bottom-2.5 right-2.5 w-9 h-9 rounded-full bg-black/45 hover:bg-rdcblue text-white flex items-center justify-center backdrop-blur transition-all z-10 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100"
        >
          <Share2 size={15} />
        </button>
        <div className="absolute bottom-2.5 left-2.5 right-12 flex items-end gap-2.5">
          <img
            src={media.logo || fallback}
            alt=""
            className="w-11 h-11 rounded-xl border-2 border-white/80 shadow-lg shrink-0 bg-white"
          />
          <div className="min-w-0 pb-0.5">
            <h3 className="font-heading font-semibold text-white text-sm truncate flex items-center gap-1">
              {media.name}
              {media.certified
                ? <BadgeCheck size={14} className="text-rdcgold fill-white shrink-0" data-testid="card-certified-badge" />
                : media.verified && <BadgeCheck size={14} className="text-rdcblue fill-white shrink-0" />}
            </h3>
            <p className="text-[11px] text-white/80 flex items-center gap-1 truncate">
              <MapPin size={10} /> {media.province}
            </p>
          </div>
        </div>
      </div>
      <div className="p-3 flex items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1 min-w-0">
          {(media.categories || []).slice(0, 2).map((c) => (
            <span key={c} className="rounded-md bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-secondary-foreground truncate">
              {c}
            </span>
          ))}
          <span className="rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary truncate">
            {(media.languages || [])[0]}
          </span>
        </div>
        <span className="flex items-center gap-1 text-[11px] text-muted-foreground shrink-0 font-mono">
          {media.is_live && media.live_viewers ? (
            <><span className="w-1.5 h-1.5 rounded-full bg-rdcred" />{media.live_viewers.toLocaleString()}</>
          ) : (
            <><Eye size={12} />{(count || 0).toLocaleString()}</>
          )}
        </span>
      </div>
      {canPreviewAudio && <audio ref={audioRef} preload="none" />}
    </Link>
  );
}
